import { useEffect, useRef, useState } from "react";
import {
  ChevronLeft20Filled,
  ChevronRight20Filled,
} from "@fluentui/react-icons";
import { ResourceSpaceType } from "../../APIService/ResourceSpaceTypeListService";
import ResourceSpaceChip from "./ResourceSpaceTypeChip";
import "./ResourceSpaceTypeList.css";

type ResourceSpaceListProps = {
  theme: string | null;
  resourceSpaceData: ResourceSpaceType[];
  selectedId?: number;
  onSelect: (item: ResourceSpaceType) => void;
};

const ResourceSpaceList = ({
  theme,
  resourceSpaceData,
  selectedId,
  onSelect,
}: ResourceSpaceListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  // drag state
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);

  const updateButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    setShowLeft(el.scrollLeft > 5);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  const scrollLeftByBtn = () => {
    scrollRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const scrollRightByBtn = () => {
    scrollRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  /* ---------------- Drag handlers ---------------- */

  const onMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;

    isDragging.current = true;
    scrollRef.current.classList.add("dragging");

    startX.current = e.pageX;
    scrollStart.current = scrollRef.current.scrollLeft;
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;

    e.preventDefault();
    const walk = (e.pageX - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollStart.current - walk;
  };

  const stopDrag = () => {
    isDragging.current = false;
    scrollRef.current?.classList.remove("dragging");
  };

  /* --------------- Touch support ---------------- */

  const onTouchStart = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;

    startX.current = e.touches[0].pageX;
    scrollStart.current = scrollRef.current.scrollLeft;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;

    const walk = (e.touches[0].pageX - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollStart.current - walk;
  };

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    updateButtons();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateButtons);
    return () => el.removeEventListener("scroll", updateButtons);
  }, []);

  return (
    <div className="list-container">
      {showLeft && (
        <button
          className={`scroll-btn-left ${theme === "dark" ? "dark" : "light"}`}
          onClick={scrollLeftByBtn}
        >
          <ChevronLeft20Filled />
        </button>
      )}

      <div
        ref={scrollRef}
        className={`resource-list ${showLeft ? "with-left" : "no-left"} ${
          showRight ? "with-right" : "no-right"
        }`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      >
        {resourceSpaceData.map((item) => (
          <ResourceSpaceChip
            key={item.id}
            theme={theme}
            resourceSpaceData={item}
            selected={selectedId === item.id}
            onSelect={onSelect}
          />
        ))}
      </div>

      {showRight && (
        <button
          className={`scroll-btn-right ${theme === "dark" ? "dark" : "light"}`}
          onClick={scrollRightByBtn}
        >
          <ChevronRight20Filled />
        </button>
      )}
    </div>
  );
};

export default ResourceSpaceList;
